/**
 * Pure-logic helpers extracted from `chat-input.tsx` (PR-AAU-B).
 *
 * Two responsibilities live in this file:
 *
 *   1. Tenant brand-id ↔ user-facing display-name mapping for the model
 *      picker. The composer currently shows up to FOUR brands:
 *
 *        - Ask Argus      (default chat brain — backend brand `argus_fast`)
 *        - Argus Pro      (greyed-out, 120B not loaded on Strix Halo)
 *        - Argus Vision   (vision-focused chat — `argus_vision`)
 *        - Argus Code     (NEW — coding-focused chat — `argus_code`)
 *
 *      The display names override whatever `displayName` comes back from
 *      `/argus-models/available`. The original API names ("ArgusFast",
 *      "ArgusPro", "ArgusVision") predate the Ask Argus rebrand and are
 *      no longer the words we want users to see.
 *
 *   2. Toggle-to-request-body serializer. The composer owns three toggles
 *      (thinkingMode, webSearch, knowledgeMode) and the parent page builds
 *      the JSON body posted to `/ecosystem/query` (or
 *      `/studio/helper/chat`). Keeping this assembly out of the React
 *      component lets us unit-test the wire shape without JSDOM.
 *
 * Everything in this file is pure functions + constants — no React.
 */

// ── Public types (also re-exported from chat-input.tsx) ─────────────────────

export type ThinkingMode = 'quick' | 'standard' | 'deep';
export type KnowledgeMode = 'library_only' | 'library_plus_ai';

export interface ChatToggles {
  thinkingMode: ThinkingMode;
  webSearch: boolean;
  knowledgeMode: KnowledgeMode;
}

/** Per-brand presentation info for the model picker. */
export interface BrandPresentation {
  /** Backend brand id — must match `/argus-models/available` items. */
  brandId: string;
  /** Founder-locked display name (overrides backend `displayName`). */
  displayName: string;
  /** When true: dim the row, disable selection, show `unavailableReason`. */
  disabled?: boolean;
  /** Tooltip text shown when `disabled` is set. */
  unavailableReason?: string;
}

// ── Brand catalog (display-side override) ───────────────────────────────────

/**
 * Founder-locked composer brand catalog. The order is the dropdown order.
 * The first entry is the DEFAULT selection (selected when no other choice
 * has been made by the user yet).
 *
 * Backend brand IDs come from `seed-argus-catalog.ts` — see KNOWN_BRANDS
 * in `studio-helper-chat.ts` for the canonical list.
 *
 * `argus_pro` is greyed-out today because the 120B weights aren't loaded
 * on the dev Strix Halo box. Once Argus Pro is warm-loadable the
 * `disabled` flag flips off without any other code change.
 */
export const COMPOSER_BRANDS: readonly BrandPresentation[] = [
  {
    brandId: 'argus_fast',
    displayName: 'Ask Argus',
  },
  {
    brandId: 'argus_pro',
    displayName: 'Argus Pro',
    disabled: true,
    unavailableReason:
      'Currently unavailable on this hardware (120B not loaded)',
  },
  {
    brandId: 'argus_vision',
    displayName: 'Argus Vision',
  },
  {
    brandId: 'argus_code',
    displayName: 'Argus Code',
  },
] as const;

/** Set of all known composer brand IDs — used for O(1) membership checks. */
const COMPOSER_BRAND_IDS = new Set<string>(COMPOSER_BRANDS.map((b) => b.brandId));

/** Default brand-id selected when nothing else applies. */
export const DEFAULT_BRAND_ID: string = COMPOSER_BRANDS[0]!.brandId;

/**
 * Translate a backend brand-id to the display name the composer should
 * render. Returns the brand-id verbatim if it's not in the COMPOSER_BRANDS
 * catalog (defensive — keeps the picker usable if the sysadmin assigns a
 * brand we haven't catalogued here yet).
 *
 * Exported for unit tests.
 */
export function brandDisplayName(brandId: string): string {
  const entry = COMPOSER_BRANDS.find((b) => b.brandId === brandId);
  return entry?.displayName ?? brandId;
}

/**
 * Look up the full presentation entry for a brand. Returns `null` for
 * brands we don't catalogue (caller can fall back to whatever shape the
 * backend gave them).
 */
export function brandPresentation(brandId: string): BrandPresentation | null {
  return COMPOSER_BRANDS.find((b) => b.brandId === brandId) ?? null;
}

/**
 * Filter the tenant-assigned brand list down to the brands the composer
 * knows how to present, in the founder-locked order, AND merge in any
 * catalogued-but-not-assigned entries that should still appear in the
 * dropdown (e.g. `argus_pro` showing as "unavailable" even when the
 * sysadmin hasn't actively assigned it — gives the user visibility into
 * the platform's roadmap without them needing to ask).
 *
 * `assignedBrandIds` is the set of brand-ids the tenant actually has
 * access to. Any brand NOT in this set is rendered but with
 * `disabled: true` (overriding the catalog `disabled` flag).
 */
export interface ComposerBrandRow extends BrandPresentation {
  /** True when the tenant has been assigned this brand by the sysadmin. */
  assigned: boolean;
}

export function buildComposerBrandList(
  assignedBrandIds: readonly string[],
): ComposerBrandRow[] {
  const assigned = new Set(assignedBrandIds);
  return COMPOSER_BRANDS.map((b): ComposerBrandRow => {
    const isAssigned = assigned.has(b.brandId);
    if (isAssigned) {
      // Tenant has access — use the catalog's disabled flag (e.g. Pro's
      // 120B-not-loaded note).
      return { ...b, assigned: true };
    }
    // Tenant does NOT have access — always disable, prefer the existing
    // unavailable-reason if present, else fall back to a generic note.
    return {
      ...b,
      assigned: false,
      disabled: true,
      unavailableReason:
        b.unavailableReason ?? 'Not assigned to this tenant by the sysadmin',
    };
  });
}

/**
 * Pick a sensible initial brand for the picker, given the tenant's
 * assigned brand list. Preference order:
 *
 *   1. The composer DEFAULT (Ask Argus / argus_fast) if assigned.
 *   2. The first composer brand the tenant is assigned to.
 *   3. The first tenant-assigned brand that isn't in our catalog (so the
 *      picker is never empty when the sysadmin assigns something exotic).
 *   4. `null` (picker is hidden).
 */
export function pickInitialBrandId(
  assignedBrandIds: readonly string[],
): string | null {
  if (assignedBrandIds.includes(DEFAULT_BRAND_ID)) return DEFAULT_BRAND_ID;
  for (const b of COMPOSER_BRANDS) {
    if (assignedBrandIds.includes(b.brandId)) return b.brandId;
  }
  const fallback = assignedBrandIds.find((id) => !COMPOSER_BRAND_IDS.has(id));
  return fallback ?? null;
}

// ── Toggle defaults + serializer ────────────────────────────────────────────

export const DEFAULT_TOGGLES: ChatToggles = {
  thinkingMode: 'standard',
  webSearch: false,
  // Founder decision 2026-06-03 (source-attribution): the default is the BLEND —
  // Library (verified) + AI (model knowledge), each attributed by source tier in
  // the answer + Sources panel. Library-Only is an opt-in stricter mode.
  knowledgeMode: 'library_plus_ai',
};

/**
 * Assemble the wire-shape `toggles` object the backend expects on
 * `/studio/helper/chat` (PR-AAU-A schema) and on `/ecosystem/query` once
 * AAU-A2 extends that route. The shape is identical at both endpoints —
 * we centralise it here so the composer + page handlers agree.
 *
 * NOTE: We always emit a fully-shaped object even when every value
 *       equals its default. This matches PR-AAU-A's `HelperTogglesSchema`,
 *       which applies `.default(...)` per-field — sending all three keys
 *       makes the request body self-documenting in audit logs.
 *
 * Exported for unit tests.
 */
export function serializeTogglesForRequest(
  toggles: ChatToggles,
): { thinkingMode: ThinkingMode; webSearch: boolean; knowledgeMode: KnowledgeMode } {
  return {
    thinkingMode: toggles.thinkingMode,
    webSearch: toggles.webSearch,
    knowledgeMode: toggles.knowledgeMode,
  };
}

/**
 * The full chat-submit context the composer hands to the parent page on
 * submit. Mirrors the legacy shape (agent + model + thinkingMode) plus
 * the new toggle fields.
 */
export interface ChatSubmitContext {
  agent: string;
  model: string;
  /** Legacy alias for `toggles.thinkingMode` — kept for back-compat. */
  thinkingMode: ThinkingMode;
  toggles: ChatToggles;
  attachedFiles?: File[];
}
